// src/v2/reviews/ReviewsRegisterPage.jsx
//
// P-19 S1: Reviews surface — manual check-off register.
// Route: /reviews
//
// Renders the config-ordered monthly Portfolio Command agenda.
// Each row has a checkbox, item name, bucket chip, cadence, status pill, and reviewer attribution.
// Checking/unchecking writes a portfolio_reviews row + audit_log entry via the API.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { T } from "../../app/tokens.js";
import { getReviews, checkReviewItem, uncheckReviewItem } from "../../data/reviewsClient.js";
import { showToast } from "../../components/toastService.jsx";

// ─── Bucket config ────────────────────────────────────────────────────────────

const BUCKET_META = {
  lease_windows: {
    label:   "Lease windows",
    cadence: "monthly",
    chipStyle: { background: T.reviewLeaseChipBg, color: T.reviewLeaseChipFg },
    chipLabel: "Lease",
  },
  plan_gaps: {
    label:   "Plan gaps",
    cadence: "monthly",
    chipStyle: { background: T.reviewPlanChipBg, color: T.reviewPlanChipFg },
    chipLabel: "Plan",
  },
  data_health: {
    label:   "Data health",
    cadence: "monthly",
    chipStyle: { background: T.reviewDataChipBg, color: T.reviewDataChipFg },
    chipLabel: "Data",
  },
  at_risk_projects: {
    label:   "At-risk projects",
    cadence: "monthly",
    chipStyle: { background: T.reviewOverdueBg, color: T.reviewOverdueFg },
    chipLabel: "Project",
  },
};

const BUCKET_ORDER = ["lease_windows", "plan_gaps", "data_health", "at_risk_projects"];

// ─── Status pill ──────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  reviewed: { background: T.ragGreenBg,    color: T.ragGreenText },
  due:      { background: T.reviewPlanChipBg, color: T.reviewPlanChipFg },
  overdue:  { background: T.reviewOverdueBg,  color: T.reviewOverdueFg },
};

const STATUS_LABELS = {
  reviewed: "Reviewed",
  due:      "Due",
  overdue:  "Overdue",
};

function StatusPill({ status }) {
  const style = STATUS_STYLES[status] ?? STATUS_STYLES.due;
  return (
    <span style={{
      fontSize: T.fsXxs, fontWeight: T.fwBold,
      padding: "3px 8px", borderRadius: 6,
      whiteSpace: "nowrap", width: 74, textAlign: "center",
      flexShrink: 0,
      ...style,
    }}>
      {STATUS_LABELS[status] ?? "Due"}
    </span>
  );
}

// ─── Bucket chip ──────────────────────────────────────────────────────────────

function BucketChip({ bucket }) {
  const meta = BUCKET_META[bucket];
  if (!meta) return null;
  return (
    <span style={{
      display: "inline-block",
      fontSize: T.fsXxxs5, fontWeight: T.fwBold,
      padding: "2px 7px", borderRadius: 5, marginRight: 6,
      ...meta.chipStyle,
    }}>
      {meta.chipLabel}
    </span>
  );
}

// ─── Checkbox ─────────────────────────────────────────────────────────────────

function ReviewCheckbox({ checked, disabled, onChange }) {
  return (
    <button
      type="button"
      aria-checked={checked}
      role="checkbox"
      disabled={disabled}
      onClick={disabled ? undefined : onChange}
      style={{
        width: 20, height: 20,
        borderRadius: 6,
        border: checked
          ? `2px solid ${T.ragGreenText}`
          : `2px solid ${T.borderDark}`,
        background: checked ? T.ragGreenText : "transparent",
        color: T.white,
        fontSize: T.fsBase, fontWeight: T.fwBold,
        display: "grid", placeItems: "center",
        cursor: disabled ? "default" : "pointer",
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
        outline: "none",
      }}
    >
      {checked ? "✓" : null}
    </button>
  );
}

// ─── Date formatter ───────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return null;
  const iso = d instanceof Date ? d.toISOString().slice(0, 10) : String(d).slice(0, 10);
  if (!iso) return null;
  const dt = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

// ─── Review row ───────────────────────────────────────────────────────────────

function ReviewRow({ item, canWrite, onToggle, pending, isFocused }) {
  const reviewed = item.status === "reviewed";
  const dateStr  = reviewed && item.reviewedAt ? fmtDate(item.reviewedAt) : null;
  const meta     = BUCKET_META[item.bucket];
  const cadenceLabel = meta?.cadence ?? item.cadence ?? "-";
  const rowRef   = useRef(null);

  // Scroll the focused row into view once it is mounted.
  // Avoid useSearchParams (nav-trap risk); focusItemRef is read from window.location.search
  // in ReviewsRegisterPage and passed down as isFocused.
  useEffect(() => {
    if (isFocused && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isFocused]);

  return (
    <div
      ref={rowRef}
      data-testid={`review-row-${item.itemRef}`}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "11px 18px",
        borderTop: `1px solid ${T.border}`,
        opacity: pending ? 0.6 : 1,
        transition: "opacity 150ms, background 200ms",
        background: isFocused ? T.reviewPlanChipBg : "transparent",
      }}
    >
      {/* Checkbox */}
      <ReviewCheckbox
        checked={reviewed}
        disabled={!canWrite || pending}
        onChange={() => onToggle(item.itemRef, reviewed)}
      />

      {/* Item name + meta line */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: T.fwBold,
          fontSize: T.fsSm,
          color: reviewed ? T.mid : T.dark,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
           {item.navTarget ? (
             <a
               href={item.navTarget}
               onClick={(event) => event.stopPropagation()}
               style={{ color: "inherit", textDecoration: "none" }}
             >
               {item.label}
             </a>
           ) : item.label}
        </div>
        <div style={{ color: T.light, fontSize: T.fsXs, marginTop: 1 }}>
          <BucketChip bucket={item.bucket} />
          {reviewed && dateStr && item.reviewedBy
            ? `Reviewed ${dateStr} by ${item.reviewedBy}`
            : reviewed && dateStr
            ? `Reviewed ${dateStr}`
             : item.meta?.evidenceState === "complete"
             ? "Evidence found this cycle"
             : item.meta?.checkId === "spacebase_pull"
             ? "No Spacebase pull found this cycle"
            : "Due now"}
        </div>
      </div>

      {/* Cadence label */}
      <span style={{
        fontSize: T.fsXs, color: T.mid,
        width: 70, textAlign: "right", flexShrink: 0,
        textTransform: "capitalize",
      }}>
        {cadenceLabel}
      </span>

      {/* Status pill */}
      <StatusPill status={item.status} />
    </div>
  );
}

// ─── Bucket section ───────────────────────────────────────────────────────────

function BucketSection({ bucket, items, canWrite, pendingRefs, onToggle, focusItemRef }) {
  const meta = BUCKET_META[bucket];
  if (!meta || items.length === 0) return null;

  return (
    <div>
      <div style={{
        fontSize: T.fsXxxs5, letterSpacing: ".05em",
        textTransform: "uppercase", color: T.light,
        fontWeight: T.fwBold, padding: "10px 18px 4px",
      }}>
        {meta.label} · {meta.cadence}
      </div>
      {items.map((item) => (
        <ReviewRow
          key={item.itemRef}
          item={item}
          canWrite={canWrite}
          pending={pendingRefs.has(item.itemRef)}
          onToggle={onToggle}
          isFocused={focusItemRef === item.itemRef}
        />
      ))}
    </div>
  );
}

// ─── Summary score ────────────────────────────────────────────────────────────

function ReviewScore({ summary }) {
  if (summary.total === 0) {
    return <div data-testid="reviews-no-score" style={{ color: T.mid, fontSize: T.fsSm }}>No reviews due</div>;
  }
  const pct = summary.total > 0
    ? Math.round((summary.reviewed / summary.total) * 100)
    : 0;
  const color = pct >= 80 ? T.ragGreenText : pct >= 50 ? T.reviewPlanChipFg : T.reviewOverdueFg;
  return (
    <div style={{ textAlign: "right" }}>
      <div style={{ fontSize: T.fsLgX5, fontWeight: T.fwBold, color, lineHeight: 1 }}>
        {summary.reviewed} / {summary.total}
      </div>
      <div style={{ fontSize: T.fsXxs5, color: T.light }}>{pct}% reviewed</div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {string} [props.currentRole]  "admin"|"editor"|"viewer"|"unknown"|null
 */
export function ReviewsRegisterPage({ currentRole }) {
  const [data,        setData]        = useState(null);   // { cycle, items, summary }
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [pendingRefs, setPendingRefs] = useState(new Set());

  // Read the focus itemRef from URL search params written by AppShell when navigating
  // from a Portfolio Command opportunity row. Read once on mount via window.location.search
  // (not useSearchParams — nav-trap risk in React 18 routed pages).
  const [focusItemRef] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("itemRef") || null;
    } catch {
      return null;
    }
  });

  const configuredRole = data?.config?.checkoffRole ?? "editor";
  const canWrite = configuredRole === "admin"
    ? currentRole === "admin"
    : currentRole === "admin" || currentRole === "editor";

  // ── Load ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getReviews()
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(err.message || "Failed to load reviews."); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  // ── Toggle handler ──────────────────────────────────────────────────────────
  const handleToggle = useCallback(async (itemRef, wasReviewed) => {
    setPendingRefs((prev) => new Set([...prev, itemRef]));

    // Optimistic update.
    setData((prev) => {
      if (!prev) return prev;
      const items = prev.items.map((it) =>
        it.itemRef === itemRef
          ? { ...it, status: wasReviewed ? "due" : "reviewed", reviewedAt: wasReviewed ? null : new Date().toISOString(), reviewedBy: wasReviewed ? null : "You" }
          : it
      );
      const reviewed = items.filter((it) => it.status === "reviewed").length;
      return { ...prev, items, summary: { ...prev.summary, reviewed } };
    });

    try {
      if (wasReviewed) {
        await uncheckReviewItem(itemRef);
      } else {
        const result = await checkReviewItem(itemRef);
        // Update with real server timestamp.
        setData((prev) => {
          if (!prev) return prev;
          const items = prev.items.map((it) =>
            it.itemRef === itemRef
              ? { ...it, status: "reviewed", reviewedAt: result.reviewedAt }
              : it
          );
          return { ...prev, items };
        });
      }
    } catch (err) {
      // Revert optimistic update.
      setData((prev) => {
        if (!prev) return prev;
        const items = prev.items.map((it) =>
          it.itemRef === itemRef
            ? { ...it, status: wasReviewed ? "reviewed" : "due", reviewedAt: wasReviewed ? it.reviewedAt : null }
            : it
        );
        const reviewed = items.filter((it) => it.status === "reviewed").length;
        return { ...prev, items, summary: { ...prev.summary, reviewed } };
      });
      showToast(err.message || "Failed to update review status.", { type: "error" });
    } finally {
      setPendingRefs((prev) => {
        const next = new Set(prev);
        next.delete(itemRef);
        return next;
      });
    }
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────────

  // Group items by bucket in the defined order.
  const agendaOrder = Array.isArray(data?.config?.agenda) ? data.config.agenda : BUCKET_ORDER;
  const itemsByBucket = {};
  for (const bucket of agendaOrder) itemsByBucket[bucket] = [];
  if (data?.items) {
    for (const item of data.items) {
      if (itemsByBucket[item.bucket]) {
        itemsByBucket[item.bucket].push(item);
      }
    }
  }

  return (
    <div
      data-testid="reviews-register-page"
      style={{ padding: "24px", maxWidth: 960, margin: "0 auto" }}
    >
      {/* Error */}
      {error && (
        <div style={{
          padding: 16, background: T.ragRedBg, borderRadius: 8,
          color: T.red, fontSize: T.fsSm, marginBottom: 20,
        }}>
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{
          padding: "40px 0", textAlign: "center",
          color: T.mid, fontSize: T.fsSm, fontStyle: "italic",
        }}>
          Loading reviews…
        </div>
      )}

      {/* Register panel */}
      {!loading && !error && data && (
        <div style={{
          background: T.card,
          border: `1px solid ${T.border}`,
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 8px 30px rgba(15,23,42,.08)",
        }}>
          {/* Panel header */}
          <div style={{
            display: "flex", alignItems: "center",
            justifyContent: "space-between",
            padding: "15px 18px",
            borderBottom: `1px solid ${T.border}`,
          }}>
            <div>
              <div style={{ fontWeight: T.fwDisplay, fontSize: T.fsLg5, color: T.dark }}>
                Reviews
              </div>
              <div style={{ color: T.mid, fontSize: T.fsSm, marginTop: 2 }}>
                 {data.cycle} · monthly agenda · check off as you go
              </div>
            </div>
            <ReviewScore summary={data.summary} />
          </div>

          {/* Bucket sections */}
          {agendaOrder.map((bucket) => (
            <BucketSection
              key={bucket}
              bucket={bucket}
              items={itemsByBucket[bucket] ?? []}
              canWrite={canWrite}
              pendingRefs={pendingRefs}
              onToggle={handleToggle}
              focusItemRef={focusItemRef}
            />
          ))}

          {/* Empty state */}
          {data.items.length === 0 && (
            <div style={{
              padding: "40px 20px", textAlign: "center",
              color: T.mid, fontSize: T.fsSm, fontStyle: "italic",
            }}>
              No review items found for this cycle.
            </div>
          )}

          {/* Footer */}
          <div style={{
            padding: "12px 18px",
            borderTop: `1px solid ${T.border}`,
            fontSize: T.fsXs, color: T.light,
          }}>
            Checking a box records who + when to audit_log.
            {!canWrite && (
              <span style={{ marginLeft: 8, fontStyle: "italic" }}>
                You have view-only access.
              </span>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
